/**
 * The search filter's URL round trip.
 *
 * WHY THE URL AT ALL: a filtered view is something people send each other.
 * «این لیست رو ببین» with a link that opens the same twelve contacts is worth
 * more than any amount of in-app state, and it also makes the browser's back
 * button do the obvious thing after a drill-down.
 *
 * ONE MODULE, because the round trip has to be exact in both directions and
 * there are four callers: the workbench reads it on mount, writes it on every
 * change, hands it to the CSV route, and hands it to campaign creation. Four
 * ad-hoc serializers is how the export ends up covering a different set than
 * the table it was launched from.
 *
 * The shape written to the URL is the GraphQL `ContactFilterInput` shape
 * (camelCase), not the CRM's snake_case `filter_json`. That is deliberate: what
 * the client holds is what it sends, and the BFF owns the conversion. A client
 * that spoke the domain service's dialect would be a client that knows about
 * the domain service.
 */

/** The empty filter — what "no filters applied" means, in one place. */
export const EMPTY_FILTER = {
  term: '',
  kinds: [],
  // NOTE: `collaborationStatuses` and `subscriptionTypes` were REMOVED from the
  // contacts filter on request. Dropped from the model and not merely from the
  // panel, deliberately: leaving the keys here would let a bookmarked URL or a
  // saved view narrow the list by a filter no control can show or clear, which
  // is the one thing this panel's design refuses to do. The underlying fields
  // are untouched everywhere else — the profile, the status history, the bulk
  // action, the analytics funnel and the messaging rules all still use them.
  fieldOfWorkIds: [],
  specialtyIds: [],
  tagIds: [],
  tagMatch: 'ANY',
  productIds: [],
  // --- the CRM profile dimensions (migration 0016) ---
  lifecycleStageIds: [],
  salesStageIds: [],
  customerHealthIds: [],
  engagementStatusIds: [],
  cityIds: [],
  facilityTypeIds: [],
  // «ارزیابی» — the tags flagged isAssessment. A separate list from `tagIds`
  // because tags OR under `tagMatch` and an assessment has to AND with them.
  assessmentTagIds: [],
  genders: [],
  referrerContactId: '',
  // TRI-STATE: null = everyone, true = «مشتری ویژه», false = «عادی». A bool
  // could not say "everyone", which is the default and the usual answer.
  isGold: null,
  createdFrom: '',
  createdTo: '',
  lastInteractionFrom: '',
  lastInteractionTo: '',
  hasNoInteractionSinceDays: null,
  affiliatedOrgId: '',
  includeDeleted: false,
  sort: 'NAME',
  sortDesc: false,
};

const LIST_KEYS = [
  'kinds',
  'fieldOfWorkIds',
  'specialtyIds',
  'tagIds',
  'productIds',
  'lifecycleStageIds',
  'salesStageIds',
  'customerHealthIds',
  'engagementStatusIds',
  'cityIds',
  'facilityTypeIds',
  'assessmentTagIds',
  'genders',
];
const TEXT_KEYS = [
  'term',
  'createdFrom',
  'createdTo',
  'lastInteractionFrom',
  'lastInteractionTo',
  'affiliatedOrgId',
  'referrerContactId',
];

/** Is this filter selecting everything? Sort and visibility are not filters. */
export function isEmptyFilter(filter) {
  const f = { ...EMPTY_FILTER, ...(filter ?? {}) };
  return (
    !f.term.trim() &&
    LIST_KEYS.every((key) => (f[key] ?? []).length === 0) &&
    TEXT_KEYS.slice(1).every((key) => !f[key]) &&
    !f.hasNoInteractionSinceDays &&
    // `== null` and not falsy: «عادی» is `false`, a real selection that a
    // truthiness test would read as "not filtering".
    f.isGold == null
  );
}

/** How many distinct filters are applied — for the "N فیلتر" badge. */
export function countActiveFilters(filter) {
  const f = { ...EMPTY_FILTER, ...(filter ?? {}) };
  let count = 0;
  if (f.term.trim()) count += 1;
  for (const key of LIST_KEYS) if ((f[key] ?? []).length > 0) count += 1;
  for (const key of TEXT_KEYS.slice(1)) if (f[key]) count += 1;
  if (f.hasNoInteractionSinceDays) count += 1;
  if (f.isGold != null) count += 1;
  if (f.includeDeleted) count += 1;
  return count;
}

/**
 * Filter -> URLSearchParams.
 *
 * Only non-default values are written, so a plain `/contacts` stays plain and
 * a shared link carries exactly what the sender had set. Lists are
 * comma-joined rather than repeated, because `?tagIds=a,b` is legible in a chat
 * message and `?tagIds=a&tagIds=b` is not.
 */
export function filterToSearchParams(filter, extra = {}) {
  const f = { ...EMPTY_FILTER, ...(filter ?? {}) };
  const params = new URLSearchParams();

  if (f.term.trim()) params.set('q', f.term.trim());
  for (const key of LIST_KEYS) {
    const values = (f[key] ?? []).filter(Boolean);
    if (values.length) params.set(key, values.join(','));
  }
  for (const key of TEXT_KEYS.slice(1)) {
    if (f[key]) params.set(key, f[key]);
  }
  if (f.tagMatch === 'ALL') params.set('tagMatch', 'ALL');
  if (f.hasNoInteractionSinceDays) {
    params.set('quietDays', String(f.hasNoInteractionSinceDays));
  }
  if (f.isGold != null) params.set('gold', f.isGold ? '1' : '0');
  if (f.includeDeleted) params.set('deleted', '1');
  if (f.sort && f.sort !== 'NAME') params.set('sort', f.sort);
  if (f.sortDesc) params.set('desc', '1');

  for (const [key, value] of Object.entries(extra)) {
    if (value !== null && value !== undefined && value !== '' && value !== 1) {
      params.set(key, String(value));
    }
  }
  return params;
}

/** URLSearchParams -> filter. Unknown parameters are ignored. */
export function filterFromSearchParams(params) {
  const get = (key) => params.get(key) ?? '';
  const list = (key) =>
    get(key)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  const days = Number.parseInt(get('quietDays'), 10);
  return {
    ...EMPTY_FILTER,
    term: get('q'),
    ...Object.fromEntries(LIST_KEYS.map((key) => [key, list(key)])),
    ...Object.fromEntries(TEXT_KEYS.slice(1).map((key) => [key, get(key)])),
    tagMatch: get('tagMatch') === 'ALL' ? 'ALL' : 'ANY',
    hasNoInteractionSinceDays: Number.isFinite(days) && days > 0 ? days : null,
    // Three states from one parameter: absent, '1' and '0'. Anything else is
    // treated as absent, so a mangled link opens unfiltered rather than
    // silently filtered to «عادی».
    isGold: get('gold') === '1' ? true : get('gold') === '0' ? false : null,
    includeDeleted: get('deleted') === '1',
    sort: ['NAME', 'CREATED', 'LAST_INTERACTION', 'STATUS'].includes(get('sort'))
      ? get('sort')
      : 'NAME',
    sortDesc: get('desc') === '1',
  };
}

/**
 * Filter -> the GraphQL variable.
 *
 * Empty strings and empty lists become null/undefined rather than being sent —
 * the BFF treats an empty term as "no term", but sending one anyway would put
 * `term: ""` in every stored saved view.
 */
export function toFilterInput(filter) {
  const f = { ...EMPTY_FILTER, ...(filter ?? {}) };
  const input = {};
  if (f.term.trim()) input.term = f.term.trim();
  for (const key of LIST_KEYS) {
    const values = (f[key] ?? []).filter(Boolean);
    if (values.length) input[key] = values;
  }
  for (const key of TEXT_KEYS.slice(1)) {
    if (f[key]) input[key] = f[key];
  }
  if (f.tagMatch === 'ALL') input.tagMatch = 'ALL';
  if (f.hasNoInteractionSinceDays) {
    input.hasNoInteractionSinceDays = f.hasNoInteractionSinceDays;
  }
  if (f.isGold != null) input.isGold = f.isGold;
  if (f.includeDeleted) input.includeDeleted = true;
  if (f.sort && f.sort !== 'NAME') input.sort = f.sort;
  if (f.sortDesc) input.sortDesc = true;
  return input;
}

/**
 * A saved view's stored JSON -> a filter.
 *
 * The server stores the CRM's snake_case shape (it re-parses and re-serializes
 * on save), so opening a saved view means translating back. The key map is
 * written out rather than derived, because a mechanical camelCase conversion
 * would silently produce `hasNoInteractionSinceDays` from
 * `has_no_interaction_since_days` and nothing else — and then quietly do the
 * wrong thing for a key that does not follow the pattern.
 */
const FROM_STORED = {
  term: 'term',
  kinds: 'kinds',
  // `collaboration_statuses` and `subscription_types` are deliberately ABSENT:
  // a saved view stored before they were removed opens without them rather
  // than applying a filter the panel cannot display (see EMPTY_FILTER).
  field_of_work_ids: 'fieldOfWorkIds',
  specialty_ids: 'specialtyIds',
  tag_ids: 'tagIds',
  tag_match: 'tagMatch',
  product_ids: 'productIds',
  lifecycle_stage_ids: 'lifecycleStageIds',
  sales_stage_ids: 'salesStageIds',
  customer_health_ids: 'customerHealthIds',
  engagement_status_ids: 'engagementStatusIds',
  city_ids: 'cityIds',
  facility_type_ids: 'facilityTypeIds',
  assessment_tag_ids: 'assessmentTagIds',
  genders: 'genders',
  referrer_contact_id: 'referrerContactId',
  is_gold: 'isGold',
  created_from: 'createdFrom',
  created_to: 'createdTo',
  last_interaction_from: 'lastInteractionFrom',
  last_interaction_to: 'lastInteractionTo',
  has_no_interaction_since_days: 'hasNoInteractionSinceDays',
  affiliated_org_id: 'affiliatedOrgId',
  include_deleted: 'includeDeleted',
  sort: 'sort',
  sort_desc: 'sortDesc',
};

export function filterFromStoredJson(json) {
  let stored;
  try {
    stored = typeof json === 'string' ? JSON.parse(json) : (json ?? {});
  } catch {
    // A stored view whose JSON is unreadable opens EMPTY rather than throwing:
    // a broken bookmark should not take the screen down.
    return { ...EMPTY_FILTER };
  }
  const filter = { ...EMPTY_FILTER };
  for (const [storedKey, key] of Object.entries(FROM_STORED)) {
    if (stored[storedKey] !== undefined) filter[key] = stored[storedKey];
  }
  // Dates arrive as full ISO timestamps; the date inputs want YYYY-MM-DD.
  for (const key of ['createdFrom', 'createdTo', 'lastInteractionFrom', 'lastInteractionTo']) {
    if (filter[key]) filter[key] = String(filter[key]).slice(0, 10);
  }
  return filter;
}
